/**
 * The company's own settings: the tenant row and the workspace modules.
 */
import { api, put } from '@/api/client';
import type { OpeningHours, RuleSector } from '@/shell/tenant';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { BreakMode } from '@timefly/core';

/** The company's push settings (backend services/pushKinds.js); null on the tenant means the defaults. */
export interface PushSettings {
  off: string[];
  shift_lead_minutes: number;
  daily_at: string;
}

export interface TenantWrite {
  name?: string;
  longitude?: number | null;
  latitude?: number | null;
  is_gps_enabled?: boolean;
  lunch_included?: boolean;
  break_mode?: BreakMode;
  lunch_length_minutes?: number | null;
  shift_length_hours?: number | null;
  /** the company's full-time week, at most 40; null falls back to 37.5 (plan 0027 B) */
  agreed_week_hours?: number | string | null;
  /** unused timebank hours: 'pay' on avspasering_payout_day ('MM-DD'), or 'carry' (plan 0027 G2) */
  avspasering_unused?: 'pay' | 'carry' | null;
  avspasering_payout_day?: string | null;
  /** the payroll code paid-out timebank hours go out under */
  avspasering_payout_code?: string | null;
  /** lønnsart for ordinary hours; null reads as 100 (2026-09-28) */
  regular_pay_code?: string | null;
  /** lønnsart for the 1 and 17 May supplement; null leaves the line without one (#26, 2026-10-12) */
  hoytid_pay_code?: string | null;
  /** #143: an exempt person (AML §10-12) on a fixed salary gets no supplement lines either; off by default */
  exempt_salaried_no_supplements?: boolean;
  merarbeid_plain_pay?: boolean;
  /** Innstillinger → Lønn «Lønnssystem» (#110): which payroll system's file Timer til lønn makes; 'unchosen' until a new company picks, null for «Annet» */
  payroll_system?: 'tripletex' | 'poweroffice' | 'visma' | 'unchosen' | null;
  /** plan 0027 batch C: the organisation number, what the Enhetsregisteret says for it, the tariff flag and the 52-week period start */
  org_number?: string | null;
  tariff_bound?: boolean;
  /** issue #70: availability an employee enters waits for a manager's approval */
  availability_requires_approval?: boolean;
  /** Innstillinger → Prosjekter (#115) */
  projects_required?: boolean;
  projects_label?: 'Prosjekt' | 'Oppdrag' | 'Objekt' | null;
  /** #132: the share of a project's budgeted hours where it warns; null is 80 */
  budget_warn_percent?: number | null;
  overtime_period_start?: string | null;
  /** Innstillinger → Varsler (#124, #133): the company's push switches and times */
  push_settings?: PushSettings;
  /** plan 0028 slice 3: the first-run setup moves on, or ends with null */
  setup_step?: 'settings' | 'company' | 'employees' | 'rules' | 'hugin' | 'review' | null;
  gps_radius_meters?: number | null;
  /** how vacation is counted from now on; absences already charged keep their charge */
  vacation_day_basis?: 'virkedager' | 'arbeidsdager';
  /** when the company is open, per weekday; null clears it back to unset (2026-09-20) */
  opening_hours?: OpeningHours | null;
  /** #294 ONB-08: the rule profile; administrator only. rule_confirmed true confirms it, a change clears it */
  rule_sector?: RuleSector | null;
  rule_agreement?: 'none' | 'yes' | null;
  agreement_name?: string | null;
  rule_effective_from?: string | null;
  rule_confirmed?: boolean;
  /** #294 ONB-09: minutes a punch may differ from the plan before it is a deviation, 0 to 240 */
  punch_tolerance_minutes?: number | null;
  /** #267 SET-30: the values the page showed for the fields written; a field changed since is refused with 409 */
  expected?: Record<string, unknown>;
}
export function useTenantWrite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: TenantWrite }) => put<unknown>(`/tenant/${id}`, body),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['tenant'] }),
        qc.invalidateQueries({ queryKey: ['my-tenants'] }),
      ]),
  });
}

/** PATCH /tenant/workspace: the module booleans, the visibility switch and the revision last read; 409 on a stale revision (contract 2026-09-13; visibility 2026-09-16). */
export interface WorkspaceWrite {
  scheduling_enabled?: boolean;
  projects_enabled?: boolean;
  /** the first working day, chosen in Kom i gang (#294) */
  start_date?: string | null;
  revision: number;
}
export function useWorkspaceWrite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: WorkspaceWrite) =>
      api<{ workspace: unknown }>('/tenant/workspace', { method: 'PATCH', body: JSON.stringify(body) }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['tenant'] });
      qc.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}
